/**
 * PanelSelectionMixin — PanelSelectionService + PanelHighlightService 퍼사드 wrapper
 *
 * 모든 비즈니스 로직은 services/panelSelectionService.js + utils/panelHighlightService.js 에 위임.
 * 이 mixin은 Vue reactive 연결만 담당.
 */
import Collection from 'ol/Collection';
import { Style, Fill, Stroke, Text } from 'ol/style';
import Feature from 'ol/Feature';
import { Polygon, MultiPolygon } from 'ol/geom';
import { PanelEntityId } from '@/features/panel-planning/utils/PanelEntityId';
import {
  diffOlFeatureCollection, isMapAdditiveSelectionEvent,
  PANEL_MAP_SELECTION_MODE, pickMapClickTargetFeature,
  resolveGroupClickSelection, resolveModuleDblClickSelection,
  resolveStringSeqFromMapFeature
} from '@/features/panel-planning/utils/panelMapSelection';
import { createEmpty, extend, isEmpty, getCenter } from 'ol/extent';
import {
  resolveTreeBulkSelection,
  unionMapSelectionTargetsFromTreeItems
} from '@/features/panel-planning/utils/panelTreeBulkSelection';
import {
  createPanelMapFeatureIndex,
  getPanelMapStringAnchorForString,
  rebuildPanelMapFeatureIndex as rebuildPanelMapFeatureIndexFromFeatures
} from '@/features/panel-planning/utils/panelMapFeatureIndex';
import {
  PANEL_MAP_DBLCLICK_GUARD_MS, PANEL_MAP_PAN_DURATION_MS,
  PANEL_MAP_FIT_MAX_ZOOM, PANEL_MAP_FIT_PADDING,
  PANEL_MAP_SINGLE_CLICK_DELAY_MS,
  resolvePanelTreeSelectionCamera, resolveModuleNudgeZoom
} from '@/features/panel-planning/utils/panelMapMotion.js';
import { PANEL_HIGHLIGHT_STYLE } from '@/features/panel-planning/utils/panelHighlightStyle';
import { createPanelHighlightService } from '@/features/panel-planning/utils/panelHighlightService';
import { createPanelSelectionService } from '../services/panelSelectionService.js';
import Overlay from 'ol/Overlay';
import { MAP_HIERARCHY_BOOKMARK_OVERLAY_OPTIONS } from '@/features/dashboard-map/hierarchy/mapHierarchyBookmarkLabel';
import { createPanelHierarchyBookmarkOverlay, resolveModuleTreeItemForMapFeature } from '@/features/panel-planning/utils/panelPlanningBookmark';
import {
  readModuleSeq,
  readStringDisplayNo,
  readStringSeq
} from '@/features/panel-planning/utils/panelMapFeatureProps';
import { readStringSeqFromTreeItem } from '@/features/panel-planning/utils/panelHierarchyMappers';

export const selectSetup = {
  methods: {
    initPanelMapSelectionCollections() {
      this._selService = createPanelSelectionService();
      this.groupSelection = this._selService.getGroupSelection();
      this.moduleSelection = this._selService.getModuleSelection();
      if (!this._panelHighlightService) {
        this._panelHighlightService = createPanelHighlightService({ Style, Fill, Stroke, Text, Feature });
      }
    },

    refreshMapSelectionStyleCache() {
      if (!this.getPanelMapStyleStore) return;
      const store = this.getPanelMapStyleStore();
      store.selectionGroupSet.clear();
      store.selectedStringSeqs.clear();
      store.selectionModuleSet.clear();
      this.groupSelection?.forEach(f => {
        store.selectionGroupSet.add(f);
        const stringSeq = resolveStringSeqFromMapFeature(f);
        if (stringSeq != null) store.selectedStringSeqs.add(stringSeq);
      });
      this.moduleSelection?.forEach(f => store.selectionModuleSet.add(f));
      if (typeof this.selectionUiVersion === 'number') this.selectionUiVersion += 1;
    },

    expandSparseSelectionForHighlight(anchorFeatures) {
      return this._selService.expandSparseSelectionForHighlight(
        anchorFeatures, this.getPanelMapFeatureIndex?.()
      );
    },

    /** String Array 이동 등 — byArrayId 인덱스를 피처 solarArraySeq와 동기화 */
    rebuildPanelMapFeatureIndex() {
      if (!this.vectorSource) {
        return;
      }
      if (!this._cachedMapFeatureIndex) {
        this._cachedMapFeatureIndex = createPanelMapFeatureIndex();
      }
      rebuildPanelMapFeatureIndexFromFeatures(
        this._cachedMapFeatureIndex,
        this.vectorSource.getFeatures()
      );
    },

    setPanelMapGroupSelection(targetFeatures) {
      if (!this.groupSelection) return;
      this._selService.setGroupSelection(targetFeatures, {
        onRefresh: () => this.refreshMapSelectionStyleCache(),
        onTranslateUpdate: () => this.updateModifyTranslateFeatures?.(),
        onStyleRefresh: () => this.requestVectorStyleRefresh?.()
      });
    },

    setPanelMapModuleSelection(targetFeatures) {
      if (!this.moduleSelection) return;
      this._selService.setModuleSelection(targetFeatures, {
        onRefresh: () => this.refreshMapSelectionStyleCache(),
        onTranslateUpdate: () => this.updateModifyTranslateFeatures?.(),
        onStyleRefresh: () => this.requestVectorStyleRefresh?.()
      });
    },

    clearGroupMapSelection() {
      this._selService.clearGroup({
        onHighlightClear: () => this.removeSelectionHighlight(),
        onRefresh: () => { this.selectedGroupFeature = null; this.refreshMapSelectionStyleCache(); },
        onTranslateUpdate: () => this.updateModifyTranslateFeatures?.(),
        onStyleRefresh: () => this.requestVectorStyleRefresh?.()
      });
      this.clearPanelHierarchyBookmark();
    },

    clearModuleMapSelection() {
      this._selService.clearModule({
        onHighlightClear: () => this.removeSelectionHighlight(),
        onRefresh: () => { this.selectedModuleFeature = null; this.refreshMapSelectionStyleCache(); },
        onTranslateUpdate: () => this.updateModifyTranslateFeatures?.(),
        onStyleRefresh: () => this.requestVectorStyleRefresh?.()
      });
      this.clearPanelHierarchyBookmark();
    },

    clearAllMapSelection() {
      if (
        this.divisionPopupOpen ||
        Date.now() < (this._suppressMapSelectionUntil || 0)
      ) {
        return;
      }
      this.clearGroupMapSelection();
      this.clearModuleMapSelection();
      this.lastHighlightedItemId = null;
      this.clearStringDrawTarget?.();
    },

    clearPanelHierarchyBookmark() {
      if (this._panelHierarchyBookmarkOverlay && this.map) {
        this.map.removeOverlay(this._panelHierarchyBookmarkOverlay);
      }
      this._panelHierarchyBookmarkOverlay = null;
    },

    syncPanelHierarchyBookmarkFromTreeItem(item, ctx = {}) {
      this.clearPanelHierarchyBookmark();
      if (!item || !this.map) return;

      const built = createPanelHierarchyBookmarkOverlay(item, {
        treeData: this.treeData,
        featureIndex: this.getPanelMapFeatureIndex?.(),
        moduleFeatures: ctx.moduleFeatures,
        groupFeatures: ctx.groupFeatures
      });
      if (!built) return;

      this._panelHierarchyBookmarkOverlay = new Overlay({
        element: built.element,
        position: built.position,
        ...MAP_HIERARCHY_BOOKMARK_OVERLAY_OPTIONS
      });
      this.map.addOverlay(this._panelHierarchyBookmarkOverlay);
    },

    /** 삭제된 피처들을 선택 컬렉션에서 제거 (String/Module 삭제 흐름에서 호출) */
    removePanelMapSelectionForFeatures(payload = {}) {
      const groupF = payload.groupFeatures || [];
      const moduleF = payload.moduleFeatures || [];
      if (!groupF.length && !moduleF.length) return;
      const rg = new Set(groupF);
      const rm = new Set(moduleF);
      const stringSeqs = new Set(
        groupF
          .map(f => resolveStringSeqFromMapFeature(f))
          .filter(seq => seq != null)
      );
      if ((rg.size || stringSeqs.size) && this.groupSelection) {
        [...this.groupSelection.getArray()].forEach(f => {
          if (rg.has(f)) {
            this.groupSelection.remove(f);
            return;
          }
          const seq = resolveStringSeqFromMapFeature(f);
          if (seq != null && stringSeqs.has(seq)) {
            this.groupSelection.remove(f);
          }
        });
      }
      if (rm.size && this.moduleSelection) {
        [...this.moduleSelection.getArray()].forEach(f => {
          if (rm.has(f)) this.moduleSelection.remove(f);
        });
      }
      this.refreshMapSelectionStyleCache();
    },

    getMapClickTargetFeatureAtPixel(pixel) {
      if (!this.map || !this.vectorLayer || !Array.isArray(pixel) || pixel.length < 2) return null;
      const featuresAtPixel = this.map.getFeaturesAtPixel(pixel, {
        layerFilter: layer => layer === this.vectorLayer
      }) || [];
      return pickMapClickTargetFeature(featuresAtPixel);
    },

    // ─ highlight 위임 ─
    highlightRegionWithLabel(features, item, opts = {}) {
      this.removeSelectionHighlight();
      if (!features?.length) return;
      const svc = this._getHighlightService();
      if (!svc) return;
      this._ensureHighlightSource(svc);
      svc.highlightRegion(features, PANEL_HIGHLIGHT_STYLE[item?.type] || PANEL_HIGHLIGHT_STYLE.string, null);
      this._syncHighlightRef(svc);
    },

    removeSelectionHighlight() {
      const svc = this._getHighlightService();
      if (svc) svc.clear();
    },

    _getHighlightService() { return this._panelHighlightService || null; },
    _ensureHighlightSource(svc) { svc.setSource(this.vectorSource); },
    _syncHighlightRef() {},

    // ─ 트리→피처 매핑 위임 ─
    getMapFeaturesForTreeItem(item) {
      return this._selService.resolveMapFeaturesForTreeItem(
        item, this.getPanelMapFeatureIndex?.(), this.vectorSource
      );
    },

    resolveMapSelectionTargetsForTreeItem(item) {
      if (!item?.type) return { group: [], module: [] };
      if (item.type === 'module') {
        const { moduleFeatures } = this.getMapFeaturesForTreeItem(item);
        return { group: [], module: moduleFeatures };
      }
      const { groupFeatures } = this.getMapFeaturesForTreeItem(item);
      return { group: groupFeatures, module: null };
    },

    // ─ 나머지 mixin 전용 (Vue reactive, DOM, store) ─
    afterGroupSelectionChanged(result, options = {}) {
      if (!result || result.clear) { this.clearGroupMapSelection(); return; }
      const primary = result.primaryTarget;
      const anchorFeatures = result.nextSelection || [];
      const additiveKey = Boolean(options.additiveKey);

      if (primary && typeof this.setSelectedFeature === 'function') this.setSelectedFeature(primary, 'group');
      this.removeSelectionHighlight();
      const treeItem = !additiveKey ? this.findTreeItemByMapFeature(primary) : null;
      if (treeItem) {
        this.lastHighlightedItemId = treeItem.id;
        this.syncStringDrawTargetFromTreeItem?.(treeItem);
        if (treeItem.type === 'array') {
          const highlightFeatures = this.expandSparseSelectionForHighlight(anchorFeatures);
          this.highlightRegionWithLabel(highlightFeatures, treeItem, { skipPan: true });
        }
      } else if (primary && !additiveKey) {
        const single = this.findTreeItemByMapFeature(primary);
        if (single) {
          this.lastHighlightedItemId = single.id;
          this.syncStringDrawTargetFromTreeItem?.(single);
        }
      } else if (additiveKey) { this.lastHighlightedItemId = null; }
      if (typeof this.syncMapSelectionToTree === 'function') this.syncMapSelectionToTree(anchorFeatures);
      this.refreshMapSelectionStyleCache();
      if (typeof this.updateModifyTranslateFeatures === 'function') {
        this.updateModifyTranslateFeatures();
      }
      this.requestVectorStyleRefresh?.();

      if (treeItem) {
        const bookmarkFeatures =
          treeItem.type === 'array'
            ? this.expandSparseSelectionForHighlight(anchorFeatures)
            : anchorFeatures;
        this.syncPanelHierarchyBookmarkFromTreeItem(treeItem, {
          groupFeatures: bookmarkFeatures
        });
      } else if (!additiveKey && !primary) {
        this.clearPanelHierarchyBookmark();
      }
    },

    onMapSingleClick(event) {
      if (this._mapCtrlDragActive || this.isPasteMode || this.drawingToolbar?.activeTool) return;
      const feature = this.getMapClickTargetFeatureAtPixel(event?.pixel);
      if (!feature) { this.clearAllMapSelection(); return; }
      this.handleGroupMapSingleClick(feature, { additiveKey: isMapAdditiveSelectionEvent(event) });
    },

    /** plugin ctx bridge — hitFeature는 이미 픽셀에서 resolve된 OL Feature */
    handleGroupMapSingleClick(hitFeature, options = {}) {
      if (!this.groupSelection || !this.vectorSource || !hitFeature) {
        return;
      }

      const additiveKey = Boolean(options.additiveKey ?? options.additive);
      const result = resolveGroupClickSelection({
        hitFeature,
        allFeatures: this.vectorSource.getFeatures(),
        relatedFeaturesForStringSeq: stringSeq => {
          const index = this.getPanelMapFeatureIndex?.();
          if (stringSeq != null && index) {
            const anchor = getPanelMapStringAnchorForString(index, String(stringSeq));
            if (anchor) return [anchor];
          }
          return this.vectorSource
            .getFeatures()
            .filter(
              f =>
                (f.get('featureType') || '').toLowerCase() === 'module' &&
                resolveStringSeqFromMapFeature(f) === String(stringSeq)
            );
        },
        currentSelection: this.groupSelection.getArray(),
        mode: additiveKey ? PANEL_MAP_SELECTION_MODE.TOGGLE : PANEL_MAP_SELECTION_MODE.REPLACE
      });

      if (result.clear) {
        this.clearAllMapSelection();
        return;
      }

      this.moduleSelection?.clear();
      diffOlFeatureCollection(this.groupSelection, result.nextSelection);
      this.afterGroupSelectionChanged(result, { additiveKey });
    },

    handleModuleMapDblClick(hitFeature) {
      if (!this.moduleSelection || !hitFeature) {
        return;
      }

      const result = resolveModuleDblClickSelection(hitFeature, this.moduleSelection.getArray());

      if (result.clear) {
        this.clearModuleMapSelection();
        return;
      }

      this.groupSelection?.clear();
      diffOlFeatureCollection(this.moduleSelection, result.nextSelection);

      const treeItem = resolveModuleTreeItemForMapFeature(hitFeature, this.treeData);
      if (treeItem?.id) {
        this.syncStringDrawTargetFromTreeItem?.(treeItem);
      }

      const feature = result.nextSelection[0];
      if (feature && typeof this.setSelectedFeature === 'function') {
        this.setSelectedFeature(feature, 'module');
      }

      this.removeSelectionHighlight();
      this.refreshMapSelectionStyleCache();
      if (typeof this.updateModifyTranslateFeatures === 'function') {
        this.updateModifyTranslateFeatures();
      }
      this.requestVectorStyleRefresh?.();

      if (treeItem) {
        this.syncPanelHierarchyBookmarkFromTreeItem(treeItem, {
          moduleFeatures: result.nextSelection
        });
      }
    },

    onMapDblClick(event) {
      const feature = this.getMapClickTargetFeatureAtPixel(event?.pixel);
      if (!feature) return;
      this.handleModuleMapDblClick(feature);
    },

    findTreeItemByMapFeature(feature) {
      if (!feature || !this.treeData) return null;
      const moduleSeq = readModuleSeq(feature);
      if (moduleSeq) return this.findModuleNodeByModuleSeq(moduleSeq);
      const stringSeq = resolveStringSeqFromMapFeature(feature);
      if (stringSeq) return this.findStringNodeByStringSeq(stringSeq);
      return null;
    },

    findModuleNodeByModuleSeq(moduleSeq) {
      if (!this.treeData || moduleSeq == null) return null;
      const target = String(moduleSeq);
      return this._findTreeItemRecursive(
        this.treeData,
        node =>
          node?.type === 'module' &&
          String(node.solarModuleSeq ?? node.seq ?? '') === target
      );
    },

    findStringNodeByStringSeq(stringSeq) {
      if (!this.treeData || stringSeq == null) return null;
      const seqStr = String(stringSeq);
      for (const arrayNode of this.treeData) {
        if (!arrayNode.children) continue;
        for (const stringNode of arrayNode.children) {
          if (readStringSeqFromTreeItem(stringNode) === seqStr) return stringNode;
        }
      }
      return null;
    },

    findStringNodeByStringId(stringId) {
      if (!this.treeData) return null;
      for (const arrayNode of this.treeData) {
        if (!arrayNode.children) continue;
        for (const stringNode of arrayNode.children) {
          if (String(stringNode.stringId ?? stringNode.solarStringId ?? '') === String(stringId)) return stringNode;
        }
      }
      return null;
    },

    _findTreeItemRecursive(nodes, predicate) {
      if (!nodes) return null;
      for (const node of nodes) {
        if (predicate(node)) return node;
        if (node.children) {
          const found = this._findTreeItemRecursive(node.children, predicate);
          if (found) return found;
        }
      }
      return null;
    },

    applyTreeSelectionCamera(type, features, opts = {}) {
      if (!this.view || !features?.length) return;
      const camera = resolvePanelTreeSelectionCamera(type);
      if (camera === 'none') return;
      const ext = createEmpty();
      features.forEach(f => {
        const g = f.getGeometry();
        if (g) extend(ext, g.getExtent());
      });
      if (isEmpty(ext)) return;
      const duration = opts.duration ?? PANEL_MAP_PAN_DURATION_MS;
      if (camera === 'fit') {
        this.view.fit(ext, {
          padding: PANEL_MAP_FIT_PADDING,
          duration,
          maxZoom: PANEL_MAP_FIT_MAX_ZOOM
        });
        return;
      }
      this.view.animate({
        center: getCenter(ext),
        zoom: resolveModuleNudgeZoom(this.view.getZoom()),
        duration
      });
    },

    applyTreeItemMapSelection(item, opts = {}) {
      if (!item || !this.vectorSource) return false;
      const targets = this.resolveMapSelectionTargetsForTreeItem(item);
      const features = item.type === 'module' ? targets.module || [] : targets.group || [];
      if (!features.length) return false;
      if (item.type === 'module') this.setPanelMapModuleSelection(features);
      else this.setPanelMapGroupSelection(features);
      this.removeSelectionHighlight();
      if (item.type === 'array') {
        const hf = this.expandSparseSelectionForHighlight(features);
        this.highlightRegionWithLabel(hf, item, { ...opts, skipPan: true });
      }
      this.lastHighlightedItemId = item.id;
      if (!opts.skipPan) {
        this.applyTreeSelectionCamera(item.type, features, opts);
      }
      const bookmarkCtx =
        item.type === 'module'
          ? { moduleFeatures: features }
          : {
              groupFeatures:
                item.type === 'array'
                  ? this.expandSparseSelectionForHighlight(features)
                  : features
            };
      this.syncPanelHierarchyBookmarkFromTreeItem(item, bookmarkCtx);
      return true;
    },

    applyTreeItemsMapSelection(items, opts = {}) {
      const list = (items || []).filter(item => item?.id);
      if (!list.length) {
        this.clearGroupMapSelection();
        this.clearModuleMapSelection();
        return false;
      }
      if (list.length === 1) {
        return this.applyTreeItemMapSelection(list[0], opts);
      }
      if (!this.vectorSource) return false;
      const { group, module, types, useModuleSelection } = unionMapSelectionTargetsFromTreeItems(
        list,
        item => this.resolveMapSelectionTargetsForTreeItem(item)
      );
      const features = useModuleSelection ? module : group;
      if (!features.length) return false;
      if (useModuleSelection) this.setPanelMapModuleSelection(features);
      else this.setPanelMapGroupSelection(features);
      this.removeSelectionHighlight();
      const focusItem = opts.focusItem || list[list.length - 1];
      if (!useModuleSelection && types.includes('array')) {
        const hf = this.expandSparseSelectionForHighlight(features);
        this.highlightRegionWithLabel(hf, focusItem, { ...opts, skipPan: true });
      }
      this.lastHighlightedItemId = focusItem?.id ?? null;
      if (!opts.skipPan) {
        if (useModuleSelection) {
          this.applyTreeSelectionCamera('module', features, opts);
        } else if (types.includes('array')) {
          this.applyTreeSelectionCamera(
            'array',
            this.expandSparseSelectionForHighlight(features),
            opts
          );
        }
      }
      if (useModuleSelection) {
        this.syncPanelHierarchyBookmarkFromTreeItem(focusItem, { moduleFeatures: features });
      } else {
        const bookmarkFeatures = types.includes('array')
          ? this.expandSparseSelectionForHighlight(features)
          : features;
        this.syncPanelHierarchyBookmarkFromTreeItem(focusItem, { groupFeatures: bookmarkFeatures });
      }
      return true;
    },

    handleTreeNodeSelected(data) {
      const { item, event } = data || {};
      try {
        if (!item?.id) return;
        const shiftKey = Boolean(event?.shiftKey);
        const toggleKey = Boolean(event?.ctrlKey || event?.metaKey);
        const visibleRows = this.$refs.moduleVirtualTree?.getVisibleNodeRows?.() ?? [];
        const next = resolveTreeBulkSelection({
          visibleRows,
          selectedIds: this.treeBulkSelectedIds || [],
          anchorId: this.treeBulkAnchorId,
          clickedItem: item,
          shiftKey,
          toggleKey
        });
        this.treeBulkSelectedIds = next.selectedIds;
        this.treeBulkAnchorId = next.anchorId;
        this.selectionUiVersion += 1;

        if (!next.selectedIds.length) {
          this.focusedNodeId = null;
          this.lastHighlightedItemId = null;
          this.clearGroupMapSelection();
          this.clearModuleMapSelection();
          this.clearStringDrawTarget?.();
          return;
        }

        this.focusedNodeId = item.id;
        this.lastHighlightedItemId = item.id;
        if (next.selectedIds.length === 1) {
          this.syncStringDrawTargetFromTreeItem?.(item);
        }
        this.applyTreeItemsMapSelection(this.getTreeBulkSelectedItems?.() ?? [item], {
          focusItem: item
        });
      } catch (error) { console.error('TreeNode 선택 처리 중 오류:', error); }
    },

    setupMapClickSelection() {
      if (this.mapHost || this._mapClickSelectionBound) return;
      this._mapClickSelectionBound = true;
    },

    // ─ convex hull (UI 계산이므로 mixin 유지) ─
    highlightModuleGroupOutline(moduleFeatures, primary) {
      this.removeSelectionHighlight();
      if (!moduleFeatures?.length || !this.vectorSource) return;
      const shapeGeog = primary.get('stringShapeGeog');
      const rows = primary.get('rowNum') || 1;
      const cols = primary.get('colNum') || 1;
      const outerRing = shapeGeog?.coordinates?.[0];
      const name = primary.get('name') || primary.get('solarStringName') || `String ${readStringDisplayNo(primary) || ''}`;

      // ─ boundary ring computation (inlined, too complex for service) ─
      let boundaryRing = null;
      if (outerRing && outerRing.length >= 4 && rows > 0 && cols > 0) {
        const p = outerRing.slice(0, 4);
        const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
        const occupied = Array.from({ length: rows }, () => Array(cols).fill(false));
        for (const mf of moduleFeatures) {
          const geom = mf.getGeometry();
          if (!geom) continue;
          const ext = geom.getExtent();
          const cx = (ext[0] + ext[2]) / 2, cy = (ext[1] + ext[3]) / 2;
          let bestD = Infinity, br = 0, bc = 0;
          for (let r = 0; r < rows; r++)
            for (let c = 0; c < cols; c++) {
              const cellCx = (lerp(lerp(p[0], p[1], c/cols), lerp(p[3], p[2], c/cols), r/rows)[0] + lerp(lerp(p[0], p[1], (c+1)/cols), lerp(p[3], p[2], (c+1)/cols), (r+1)/rows)[0]) / 2;
              const cellCy = (lerp(lerp(p[0], p[1], c/cols), lerp(p[3], p[2], c/cols), r/rows)[1] + lerp(lerp(p[0], p[1], (c+1)/cols), lerp(p[3], p[2], (c+1)/cols), (r+1)/rows)[1]) / 2;
              const d = Math.hypot(cx - cellCx, cy - cellCy);
              if (d < bestD) { bestD = d; br = r; bc = c; }
            }
          occupied[br][bc] = true;
        }
        const edgeMap = new Map();
        const addEdge = (s, e) => { const k = `${s[0]},${s[1]}`; if (!edgeMap.has(k)) edgeMap.set(k, [s,e]); };
        for (let r = 0; r < rows; r++)
          for (let c = 0; c < cols; c++) {
            if (!occupied[r][c]) continue;
            if (r === 0 || !occupied[r-1][c]) { const t=c/cols, b=(c+1)/cols; addEdge(lerp(p[0],p[1],t), lerp(p[0],p[1],b)); }
            if (r === rows-1 || !occupied[r+1][c]) { const t=c/cols, b=(c+1)/cols; addEdge(lerp(p[3],p[2],b), lerp(p[3],p[2],t)); }
            if (c === 0 || !occupied[r][c-1]) { const l=r/rows, r_=(r+1)/rows; addEdge(lerp(p[0],p[3],r_), lerp(p[0],p[3],l)); }
            if (c === cols-1 || !occupied[r][c+1]) { const l=r/rows, r_=(r+1)/rows; addEdge(lerp(p[1],p[2],l), lerp(p[1],p[2],r_)); }
          }
        const first = [...edgeMap][0];
        if (first) {
          const ring = [[first[1][0][0], first[1][0][1]]];
          let cur = first[1][1]; let closed = false; const startKey = `${ring[0][0]},${ring[0][1]}`;
          while (!closed) {
            ring.push([cur[0], cur[1]]);
            const nk = `${cur[0]},${cur[1]}`;
            if (nk === startKey) { closed = true; break; }
            const next = edgeMap.get(nk); if (!next) break; edgeMap.delete(nk); cur = next[1];
          }
          if (ring.length >= 3) { const f_=ring[0], l_=ring[ring.length-1]; if (f_[0]!==l_[0]||f_[1]!==l_[1]) ring.push([f_[0],f_[1]]); boundaryRing = ring; }
        }
      }
      const geom = boundaryRing ? new Polygon([boundaryRing]) : new Polygon([outerRing||[[0,0],[1,0],[1,1],[0,1]]]);
      const svc = this._getHighlightService();
      if (svc) { this._ensureHighlightSource(svc); svc.highlightSingleGeometry(geom, name); }
    },

    computeConvexHull(features) {
      if (!features?.length) return null;
      const pts = [];
      for (const f of features) {
        const g = f.getGeometry();
        if (!g) continue;
        const coords = g.getType() === 'Polygon' ? g.getCoordinates()[0] : g.getFlatCoordinates();
        for (let i = 0; i < coords.length; i++) pts.push(coords[i].length === 2 ? coords[i] : [coords[i], coords[i+1]]);
      }
      if (pts.length < 3) return null;
      return [this.grahamScan(pts)];
    },

    grahamScan(points) {
      points = [...new Set(points.map(p => `${p[0]},${p[1]}`))].map(s => s.split(',').map(Number));
      if (points.length < 3) return points;
      const start = points.reduce((a, b) => (a[1] < b[1] || (a[1] === b[1] && a[0] < b[0]) ? a : b));
      points.sort((a, b) => {
        const cross = (a[0] - start[0]) * (b[1] - start[1]) - (a[1] - start[1]) * (b[0] - start[0]);
        if (cross !== 0) return -cross;
        return Math.hypot(a[0] - start[0], a[1] - start[1]) - Math.hypot(b[0] - start[0], b[1] - start[1]);
      });
      const hull = [start];
      for (let i = 1; i < points.length; i++) {
        const p = points[i];
        while (hull.length >= 2) {
          const a = hull[hull.length - 2], b = hull[hull.length - 1];
          if ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]) > 0) break;
          hull.pop();
        }
        hull.push(p);
      }
      hull.push(start);
      return hull;
    }
  }
};
